import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';

import RequireAdmin from '@/components/Auth/RequireAdmin';
import RequireAdminOrSupport from '@/components/Auth/RequireAdminOrSupport';
import RequireCoach from '@/components/Auth/RequireCoach';
import RequireCompanyAdmin from '@/components/Auth/RequireCompanyAdmin';
import RequireFeature from '@/components/Auth/RequireFeature';
import GetStartedRedirect from '@/components/GetStartedRedirect';
import Home from '@/components/Home';
import Layout from '@/components/Layouts';
import CompanyLayout from '@/components/Layouts/Company';
import GetStartedLayout from '@/components/Layouts/GetStarted';
import ManageLayout from '@/components/Layouts/Manage';
import MemberLayout from '@/components/Layouts/Member';
import { coachMatchingReleaseV2Enabled } from '@/features/coach-matching/releaseV2Flag';
import { CoachSchedulePage } from '@/features/coachSchedule';
import MemberHomeIndex from '@/features/mcpMember/home/MemberHomeIndex';
import { mcpMemberRoutes } from '@/features/mcpMember/routes';
import { InviteLanding } from '@/features/schedulingAssistant/InviteLanding';
import { schedulingAssistantRoutes } from '@/features/schedulingAssistant/routes';
import CoachAIGate from '@/pages/CoachAI/Gate';
import CompanyList from '@/pages/Companies';
import CompanyPage from '@/pages/Company';
import DiscoveryCallback from '@/pages/DiscoveryCallback';
import Documents from '@/pages/Documents';
import ExternalAppointmentAction from '@/pages/ExternalAppointmentAction';
import ExternalSeriesSessionRedirect from '@/pages/ExternalSeriesSessionRedirect';
import ExternalSessionRedirect from '@/pages/ExternalSessionRedirect';
import About from '@/pages/GetStarted/about';
import CareerGoal from '@/pages/GetStarted/careerGoal';
import CoachingStyle from '@/pages/GetStarted/coachingStyle';
import DreamJob from '@/pages/GetStarted/dreamJob';
import GetCoach from '@/pages/GetStarted/getCoach';
import MatchingForm from '@/pages/GetStarted/matchingForm';
import MembershipPaid from '@/pages/GetStarted/membershipPaid';
import MembershipSelect from '@/pages/GetStarted/membershipSelect';
import OnboardingComplete from '@/pages/GetStarted/onboardingComplete';
import OnboardingVideo from '@/pages/GetStarted/onboardingVideo';
import ScheduleSession from '@/pages/GetStarted/scheduleSession';
import Growth from '@/pages/Growth';
import Completed from '@/pages/Growth/Completed';
import Milestone from '@/pages/Growth/Milestone';
import Insights from '@/pages/Insights';
import Survey from '@/pages/Insights/Survey';
import Responses from '@/pages/Insights/Survey/Responses';
import Manage from '@/pages/Manage';
import Assistant from '@/pages/Manage/Assistant';
import Assistants from '@/pages/Manage/Assistants';
import Cards from '@/pages/Manage/Cards';
import CoachAdminProfile from '@/pages/Manage/Coach';
import CoachAiPlayground from '@/pages/Manage/CoachAiPlayground';
import CoachAiThread from '@/pages/Manage/CoachAiThread';
import Coaches from '@/pages/Manage/Coaches';
import Companies from '@/pages/Manage/Companies';
import Company from '@/pages/Manage/Company';
import DataImports from '@/pages/Manage/DataImports';
import Function from '@/pages/Manage/Function';
import FunctionDisplay from '@/pages/Manage/FunctionDisplay';
import Notifications from '@/pages/Manage/Notifications';
import PulseReportDetailPage from '@/pages/Manage/PulseReportDetail';
import TypeformAdminPage from '@/pages/Manage/Typeform';
import TypeformFormMappingsPage from '@/pages/Manage/TypeformFormMappings';
import TypeformSubmissionDetailPage from '@/pages/Manage/TypeformSubmissionDetail';
import Member from '@/pages/Member';
import MemberCoaches from '@/pages/Member/Coaches';
import SyntheticMatchingDecision from '@/pages/Member/Matching/SyntheticDecision';
import MemberOnboardingReview, {
  CoachMemberLatestOnboardingReview,
  CoachMemberOnboardingReview,
} from '@/pages/Member/OnboardingReview';
import PrivateNotes from '@/pages/Member/PrivateNotes';
import ClientProfile from '@/pages/Member/Profile';
import CoachMemberPulseReport, {
  MemberSelfPulseReport,
} from '@/pages/Member/PulseReport';
import MemberAppointments from '@/pages/MemberAppointments';
import MemberBiweeklyBooking from '@/pages/MemberBiweeklyBooking';
import MemberChemistryBooking from '@/pages/MemberChemistryBooking';
import MemberHighlightsPage from '@/pages/MemberHighlights';
import MemberReflectionArtifactPage from '@/pages/MemberReflectionArtifact';
import MemberSessionTranscriptPage from '@/pages/MemberSessionTranscript';
import MemberSingleBooking from '@/pages/MemberSingleBooking';
import Members from '@/pages/Members';
import MembershipExpired from '@/pages/MembershipExpired';
import Mfa from '@/pages/Mfa';
import NotFound from '@/pages/NotFound';
import Profile from '@/pages/Profile';
import Reset from '@/pages/Reset';
import Root from '@/pages/Root';
import Session from '@/pages/Session';
import SessionSummaryPage from '@/pages/SessionSummary';
import {
  CoachSessionsGate,
  CoachSessionsIndexRedirect,
  LegacySessionDetailRedirect,
  LegacySessionsRedirect,
  MemberSessionsIndexRedirect,
} from '@/pages/Sessions/CoachSessionsGate';
import PastSessions from '@/pages/Sessions/Past';
import UpcomingSessions from '@/pages/Sessions/Upcoming';
import SignIn from '@/pages/SignIn';
import Support from '@/pages/Support';
import Templates from '@/pages/Templates';
import Template from '@/pages/Templates/Template';
import Toolkit from '@/pages/Toolkit';
import VerifyEmail from '@/pages/VerifyEmail';
import Welcome from '@/pages/Welcome';

const MemberMatching = lazy(() => import('@/pages/Member/Matching'));

export const buildMemberMatchingRoutes = (enabled: boolean) =>
  enabled
    ? [
        {
          element: (
            <RequireAdmin>
              <Suspense fallback={null}>
                <MemberMatching />
              </Suspense>
            </RequireAdmin>
          ),
          path: 'matching',
        },
      ]
    : [];

export const AppRoutes = [
  { element: <Root />, index: true },
  { element: <SignIn />, path: 'signin' },
  { element: <Navigate to="/signin" replace />, path: 'authenticate' },
  { element: <DiscoveryCallback />, path: 'auth/discovery-callback' },
  { element: <Mfa />, path: 'mfa' },
  { element: <VerifyEmail />, path: 'verify-email' },
  { element: <Reset />, path: 'reset' },
  { element: <Support standalone />, path: 'support' },
  ...(import.meta.env.DEV
    ? [
        {
          element: <SyntheticMatchingDecision />,
          path: '/__fixtures/coach-matching-decision',
        },
      ]
    : []),
  { element: <SignIn />, path: 'coach/signin' },
  {
    element: <Navigate to="/coach/signin" replace />,
    path: 'coach/authenticate',
  },
  { element: <MembershipExpired />, path: '/membership-expired' },
  {
    element: <MemberChemistryBooking />,
    path: '/m/book-chemistry-call/:coachId',
  },
  {
    element: <MemberBiweeklyBooking />,
    path: '/m/book-biweekly/:coachId',
  },
  {
    element: <MemberSingleBooking />,
    path: '/m/book-single-session/:coachId',
  },
  ...schedulingAssistantRoutes,
  {
    element: (
      <InviteLanding idParam="appointmentId" refKind="appointment">
        <ExternalAppointmentAction />
      </InviteLanding>
    ),
    path: '/appointments/external/:appointmentId/:action',
  },
  {
    // MEN-4915: a recurring booking's description is written once on the Google
    // master, so its links address the series. Declared before the
    // per-occurrence route for readability; the two cannot collide, because a
    // series link carries one more path segment.
    element: (
      <InviteLanding idParam="seriesId" refKind="series">
        <ExternalSeriesSessionRedirect />
      </InviteLanding>
    ),
    path: '/sessions/external/series/:seriesId/:action',
  },
  {
    // Existing calendar invites carry per-occurrence links and keep working,
    // which is why this change needs no backfill.
    element: (
      <InviteLanding idParam="externalSessionId" refKind="appointment">
        <ExternalSessionRedirect />
      </InviteLanding>
    ),
    path: '/sessions/external/:externalSessionId/:action',
  },

  {
    children: [
      { element: <About />, path: 'about' },
      { element: <DreamJob />, path: 'dream-job' },
      { element: <CareerGoal />, path: 'career-goal' },
      { element: <CoachingStyle />, path: 'style' },
      { element: <OnboardingVideo />, path: 'onboarding-video' },
      { element: <MatchingForm />, path: 'matching-form' },
      { element: <MembershipSelect />, path: 'membership-select' },
      { element: <MembershipPaid />, path: 'membership-paid' },
      { element: <GetCoach />, path: 'get-coach' },
      { element: <ScheduleSession />, path: 'schedule-session' },
      { element: <OnboardingComplete />, path: 'onboarding-complete' },
      { element: <GetStartedRedirect />, index: true },
    ],
    element: <GetStartedLayout />,
    path: '/get-started',
  },
  {
    children: [
      {
        element: (
          <RequireCompanyAdmin>
            <CompanyList />
          </RequireCompanyAdmin>
        ),
        index: true,
      },
      {
        element: (
          <RequireCompanyAdmin>
            <CompanyPage />
          </RequireCompanyAdmin>
        ),
        path: ':companyId',
      },
    ],
    element: <Layout />,
    path: '/company',
  },

  {
    children: [
      { element: <MemberHomeIndex />, index: true },
      { element: <Growth />, path: 'growth' },
      { element: <Growth />, path: 'growth/completedSteps' },
      { element: <Growth />, path: 'growth/:growthPlanId' },
      { element: <Growth />, path: 'growth/:growthPlanId/completedSteps' },
      {
        element: <Milestone />,
        path: 'growth/:growthPlanId/milestone/:milestoneId',
      },
      {
        element: <Milestone />,
        path: 'growth/milestone/:milestoneId',
      },
      { element: <Completed />, path: 'growth/completed' },
      { element: <Completed />, path: 'growth/:growthPlanId/completed' },
      {
        element: <MemberReflectionArtifactPage />,
        path: 'artifacts/session-reflection/:artifactId/:dateSlug',
      },
      {
        element: <MemberReflectionArtifactPage />,
        path: 'artifacts/session-prep/:artifactId/:dateSlug',
      },
      {
        element: <MemberSessionTranscriptPage />,
        path: 'session/:sessionId/transcript/:dateSlug',
      },
      ...mcpMemberRoutes,
      { element: <MemberHighlightsPage />, path: 'highlights' },
      { element: <Insights />, path: 'insights' },
      {
        element: <MemberOnboardingReview />,
        path: 'onboarding-review/:reportId',
      },
      { element: <MemberSelfPulseReport />, path: 'pulse/:reportId' },
      { element: <Survey />, path: 'insights/:templateId/new' },
      { element: <Survey />, path: 'insights/:templateId' },
      { element: <Profile isClient />, path: 'profile' },
      { element: <Profile />, path: 'profiles-coach/:id' },
      { element: <Welcome />, path: 'welcome' },
      { element: <Support />, path: 'support' },
      { element: <MemberAppointments />, path: 'appointments' },
      { element: <MemberSessionsIndexRedirect />, path: 'sessions' },
      { element: <UpcomingSessions />, path: 'sessions/upcoming' },
      { element: <PastSessions />, path: 'sessions/past' },
      { element: <SessionSummaryPage />, path: 'sessions/:sessionId/summary' },
      { element: <Session />, path: 'sessions/:sessionId' },
      {
        element: <CoachAIGate />,
        path: 'workflow',
      },
      {
        element: <CoachAIGate />,
        path: 'workflow/:messageThreadId',
      },
    ],
    element: <Layout />,
    path: '/m',
  },

  {
    children: [
      { element: <Home />, index: true },

      { element: <Members />, path: 'members' },
      {
        children: [
          { element: <Member />, index: true },
          { element: <MemberCoaches />, path: 'coaches' },
          ...buildMemberMatchingRoutes(coachMatchingReleaseV2Enabled()),
          { element: <ClientProfile />, path: 'profile' },
          { element: <Growth />, path: 'growth' },
          { element: <Growth />, path: 'growth/completedSteps' },
          { element: <Milestone />, path: 'growth/milestone/:milestoneId' },
          { element: <Completed />, path: 'growth/completed' },
          { element: <Insights />, path: 'insights' },
          {
            element: <CoachMemberLatestOnboardingReview />,
            path: 'onboarding-review',
          },
          {
            element: <CoachMemberOnboardingReview />,
            path: 'onboarding-review/:reportId',
          },
          { element: <CoachMemberPulseReport />, path: 'pulse/:reportId' },
          { element: <Survey />, path: 'insights/:templateId' },
          { element: <Responses />, path: 'insights/:templateId/responses' },
          {
            element: (
              <RequireFeature feature="documents" redirectPath="/coach/members">
                <Documents />
              </RequireFeature>
            ),
            path: 'documents',
          },
          { element: <Navigate to="upcoming" replace />, path: 'sessions' },
          { element: <UpcomingSessions />, path: 'sessions/upcoming' },
          { element: <PastSessions />, path: 'sessions/past' },
          {
            element: <SessionSummaryPage />,
            path: 'sessions/:memberSessionId/summary',
          },
          { element: <Session />, path: 'sessions/:memberSessionId' },
          { element: <PrivateNotes />, path: 'notes' },
        ],
        element: <MemberLayout />,
        handle: { selectedUserId: 'id' },
        path: 'members/:id',
      },

      {
        children: [
          { element: <Templates />, index: true },
          { element: <Template />, path: ':templateId' },
        ],
        path: 'templates',
      },

      {
        children: [
          { element: <Manage />, index: true },
          { element: <Cards />, path: 'cards' },
          {
            element: (
              <RequireAdminOrSupport globalOnly>
                <Coaches />
              </RequireAdminOrSupport>
            ),
            path: 'coaches',
          },
          { element: <CoachAdminProfile />, path: 'coaches/:id' },
          {
            element: (
              <RequireAdmin>
                <CoachSchedulePage />
              </RequireAdmin>
            ),
            path: 'coaches/:coachId/schedule',
          },
          {
            element: (
              <RequireAdmin>
                <MemberChemistryBooking />
              </RequireAdmin>
            ),
            path: 'coaches/:coachId/members/:memberId/book-chemistry-call',
          },
          {
            element: (
              <RequireAdmin>
                <MemberSingleBooking />
              </RequireAdmin>
            ),
            path: 'coaches/:coachId/members/:memberId/book-single-session',
          },
          {
            element: (
              <RequireAdmin>
                <MemberBiweeklyBooking />
              </RequireAdmin>
            ),
            path: 'coaches/:coachId/members/:memberId/book-biweekly',
          },
          {
            element: (
              <RequireAdmin>
                <MemberSingleBooking />
              </RequireAdmin>
            ),
            path: 'coaches/:coachId/members/:memberId/book-single-session',
          },
          { element: <Companies />, path: 'companies' },
          {
            children: [
              { element: <Navigate to="current" replace />, index: true },
              { element: <Company />, path: 'current' },
              { element: <Company />, path: 'past' },
            ],
            element: <CompanyLayout />,
            path: 'companies/:companyId',
          },
          { element: <Notifications />, path: 'notifications' },
          {
            element: <Navigate to="/coach/templates" replace />,
            path: 'templates',
          },
          { element: <CoachAiPlayground />, path: 'coach-ai-playground' },
          { element: <CoachAiThread />, path: 'coach-ai-playground/:threadId' },
          { element: <DataImports />, path: 'data-imports' },
          {
            element: (
              <RequireAdmin>
                <TypeformAdminPage />
              </RequireAdmin>
            ),
            path: 'typeform',
          },
          {
            element: (
              <RequireAdmin>
                <TypeformSubmissionDetailPage />
              </RequireAdmin>
            ),
            path: 'typeform/:submissionId',
          },
          {
            element: (
              <RequireAdmin>
                <TypeformFormMappingsPage />
              </RequireAdmin>
            ),
            path: 'typeform/forms/:formId/mappings',
          },
          {
            element: (
              <RequireAdmin>
                <PulseReportDetailPage />
              </RequireAdmin>
            ),
            path: 'typeform/pulse-reports/:reportId',
          },
          { element: <Assistants />, path: 'assistants' },
          { element: <Assistant />, path: 'assistants/:assistantId' },
          { element: <Function />, path: 'assistants/:assistantId/function' },
          {
            element: <Function />,
            path: 'assistants/:assistantId/function/:functionId',
          },
          {
            element: <FunctionDisplay />,
            path: 'assistants/:assistantId/function/:functionId/display',
          },
          {
            element: <FunctionDisplay />,
            path: 'assistants/:assistantId/function/:functionId/display/:displayId',
          },
        ],
        element: (
          <RequireAdminOrSupport>
            <ManageLayout />
          </RequireAdminOrSupport>
        ),
        path: 'manage',
      },

      { element: <Profile />, path: 'profile' },
      { element: <Welcome />, path: 'welcome' },
      { element: <Toolkit />, path: 'toolkit' },
      {
        element: <MemberReflectionArtifactPage />,
        path: 'artifacts/session-reflection/:artifactId/:dateSlug',
      },
      { element: <CoachSchedulePage />, path: 'schedule' },
      // MEN-5635: a coach books for their own member from the schedule menu.
      {
        element: (
          <RequireCoach>
            <MemberChemistryBooking />
          </RequireCoach>
        ),
        path: 'schedule/members/:memberId/book-chemistry-call',
      },
      {
        element: (
          <RequireCoach>
            <MemberSingleBooking />
          </RequireCoach>
        ),
        path: 'schedule/members/:memberId/book-single-session',
      },
      {
        element: (
          <RequireCoach>
            <MemberBiweeklyBooking />
          </RequireCoach>
        ),
        path: 'schedule/members/:memberId/book-biweekly',
      },
      { element: <SessionSummaryPage />, path: 'sessions/:sessionId/summary' },
      { element: <Session />, path: 'sessions/:sessionId' },
      {
        children: [
          { element: <CoachSessionsIndexRedirect />, index: true },
          { element: <UpcomingSessions />, path: 'upcoming' },
          { element: <PastSessions />, path: 'past' },
        ],
        element: <CoachSessionsGate />,
        path: 'sessions',
      },
      {
        children: [
          {
            element: (
              <RequireFeature feature="documents" redirectPath="/coach">
                <Documents />
              </RequireFeature>
            ),
            index: true,
          },
          { element: <Templates />, path: 'templates' },
          { element: <Template />, path: 'templates/:templateId' },
        ],
        element: <Layout />,
        path: 'documents',
      },
    ],
    element: <Layout />,
    path: '/coach',
  },

  {
    children: [
      { element: <LegacySessionsRedirect tab="upcoming" />, index: true },
      { element: <LegacySessionsRedirect tab="upcoming" />, path: 'upcoming' },
      { element: <LegacySessionsRedirect tab="past" />, path: 'past' },
      {
        element: <LegacySessionDetailRedirect summary />,
        path: ':sessionId/summary',
      },
      { element: <LegacySessionDetailRedirect />, path: ':sessionId' },
    ],
    element: <Layout />,
    path: 'sessions',
  },

  {
    children: [
      {
        element: (
          <RequireFeature feature="documents">
            <Documents />
          </RequireFeature>
        ),
        index: true,
      },
      { element: <Templates />, path: 'templates' },
      { element: <Template />, path: 'templates/:templateId' },
    ],
    element: <Layout />,
    path: '/documents',
  },

  { element: <NotFound />, path: '*' },
];
